import type { GridApi } from 'ag-grid-community'

import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

/**
 * One plotted listing. The keys follow the epic's locked axes: time-to-close on
 * X, `price / typicalSellingPrice` on Y, and typicalSellingPrice feeding the
 * bubble size (MSRP is missing on 44% of plottable listings, so it can't size
 * them). Clustering derives the series' own `sizeKey` from these prices.
 */
export type DealGraphDatum = Record<string, unknown> & {
  dealRatio: number
  endTime: Date
  /**
   * The listing itself, so the tooltip derives what it shows rather than the
   * datum accumulating a copy of every field the tooltip grows to need.
   */
  listing: CombinedListingSearchResult
  typicalSellingPrice: number
}

export type DealGraphData = {
  data: DealGraphDatum[]
  /** Rows the grid is showing that the graph can't plot. */
  excludedCount: number
}

export const EMPTY_DEAL_GRAPH_DATA: DealGraphData = {
  data: [],
  excludedCount: 0,
}

/**
 * Maps one grid row to a plottable point, or `null` when it can't be plotted.
 *
 * A missing typical selling price is the only exclusion: it is both the Y
 * denominator and the bubble size, so there is nothing to place. Listings
 * priced *above* FMV are plotted normally — that is a deal ratio above 1, not
 * an error.
 *
 * The ratio's numerator is the facade's `priceForListDerivedMetrics`, which is
 * the same value the List View "Current Bid" column shows, so the graph and the
 * table cannot disagree about a listing's price.
 */
export const toDealGraphDatum = (
  listing: CombinedListingSearchResult
): DealGraphDatum | null => {
  const { endTime, typicalSellingPrice } = listing
  if (typicalSellingPrice == null || typicalSellingPrice <= 0) return null

  return {
    dealRatio: listing.priceForListDerivedMetrics / typicalSellingPrice,
    endTime,
    listing,
    typicalSellingPrice,
  }
}

/**
 * Maps the rows the grid is currently showing to chart data. Plotted points
 * plus `excludedCount` always equals the number of rows passed in.
 */
export const toDealGraphData = (
  listings: CombinedListingSearchResult[]
): DealGraphData => {
  const data: DealGraphDatum[] = []
  for (const listing of listings) {
    const datum = toDealGraphDatum(listing)
    if (datum) data.push(datum)
  }
  return { data, excludedCount: listings.length - data.length }
}

/**
 * The grid's rows after filter and after sort — the composition with every
 * existing ALP filter and sort that the graph gets for free.
 *
 * Read by index rather than `forEachNodeAfterFilterAndSort`: that API belongs
 * to `ClientSideRowModelApiModule`, which `ListViewTable` does not register, so
 * AG Grid resolves it to a no-op that only warns. `getDisplayedRowAtIndex` is
 * in `RowApiModule`, which is registered. (This is the same reason
 * `ExportListViewButton` loops by index.)
 */
export const getDisplayedListings = (
  gridApi: GridApi<CombinedListingSearchResult>
): CombinedListingSearchResult[] => {
  const listings: CombinedListingSearchResult[] = []
  const rowCount = gridApi.getDisplayedRowCount()
  for (let i = 0; i < rowCount; i++) {
    const { data } = gridApi.getDisplayedRowAtIndex(i) ?? {}
    if (data) listings.push(data)
  }
  return listings
}
